import { Box, ListItem, ListItemText } from '@mui/material';
import { ReactNode } from 'react';
import { NavLink, useMatch, useNavigate } from 'react-router-dom';

import { IconProps, IconType } from 'src/components/icons';
import { UserRole, userRoleList } from 'src/services/user';
import { RoleGuard } from 'src/services/user/RoleGuard';
import { theme } from '../../services/theme';

export interface DrawerLinkProps {
  disableForSuperAdmin?: boolean;
  icon: IconType;
  iconSx?: IconProps['sx'];
  isExternalLink?: boolean;
  match?: string;
  notifyIcon?: ReactNode;
  roles?: UserRole[];
  title: string;
  to: string;
  toggleMenu: () => void;
}

export const DrawerLink = (props: DrawerLinkProps) => {
  const Icon = props.icon;
  const matched = useMatch(props.match || `${props.to}/*`);
  const backgroundColor = matched ? '#F1F6F9' : 'transparent';
  const color = matched ? '#356482' : '#C5D9E8';
  const navigate = useNavigate();

  return (
    <RoleGuard
      disableForSuperAdmin={props.disableForSuperAdmin}
      key={props.title}
      roles={props.roles || userRoleList}
    >
      <ListItem
        button
        sx={{
          backgroundColor,
          borderRadius: '10px',
          '&:hover': { backgroundColor },
          mb: '12px',
          [theme.breakpoints.down('lg')]: {
            padding: '10px',
          },
          [theme.breakpoints.down('md')]: {
            justifyContent: 'center',
          },
          [theme.breakpoints.down('sm')]: {
            justifyContent: 'flex-start',
          },
        }}
      >
        <Box
          onClick={(e) => {
            e.preventDefault();
            props.toggleMenu();

            if (props.isExternalLink) {
              window.open(props.to, '_blank');
              return;
            }

            navigate(props.to);
          }}
          sx={{ color, textDecoration: 'none' }}
        >
          <Box
            sx={{
              alignItems: 'center',
              justifyContent: 'center',
              color,
              display: 'flex',
              textDecoration: 'none',
              [theme.breakpoints.down('md')]: {
                flexWrap: 'wrap',
              },
              [theme.breakpoints.down('sm')]: {
                flexWrap: 'nowrap',
              },
            }}
          >
            <Icon stroke={color} sx={{ fontSize: '22px', ...props.iconSx }} />
            <ListItemText
              primary={props.title}
              sx={{
                ml: '16px',
                mr: '12px',
                whiteSpace: 'nowrap',
                [theme.breakpoints.down('lg')]: {
                  ml: '7px',
                  mr: '5px',
                  '& span': {
                    fontSize: '11px',
                  },
                },
                [theme.breakpoints.down('md')]: {
                  width: '100%',
                  ml: '0',
                  mr: '0',
                  '& span': {
                    fontSize: '9px',
                    textAlign: 'center',
                  },
                },
                [theme.breakpoints.down('sm')]: {
                  ml: '7px',
                  mr: '5px',
                  '& span': {
                    fontSize: '11px',
                    textAlign: 'left',
                  },
                },
              }}
            />
            {props.to === '/shared-with-me' ? props.notifyIcon : ''}
          </Box>
        </Box>
      </ListItem>
    </RoleGuard>
  );
};
